/**
 * Shared program-wide workshop content defaults.
 *
 * Editable when a client is on "Use default content". Blob path:
 * `workshops/_shared/workshop-defaults.json` (not client-scoped). IDB key
 * is also unscoped — program data, not per-client.
 */

import { create } from "zustand";
import { get as idbGet, set as idbSet } from "idb-keyval";
import type { Workshop } from "@/content/workshops";
import { isWorkshopBffEnabled } from "@/env";
import { broadcastSync } from "@/lib/sync-broadcast";
import {
  DocsConflictError,
  getJsonWithEtag,
  isDocsAuthReady,
  putJsonWithEtag,
} from "@/lib/docs-api";
import {
  OVERRIDE_ID,
  sanitiseOverride,
  type WorkshopOverride,
  type WorkshopOverridePatch,
} from "@/features/workshop/overrides/workshop-override-content";
import type { SyncState } from "@/lib/bff-state-doc";

const IDB_KEY = "xaes-workshop-content-defaults";
const BFF_PATH = "_shared/workshop-defaults.json";
const WORKSHOPS_TIER = "workshops";

const WORKSHOP_IDS: Array<Workshop["id"]> = [
  "WS1",
  "WS2",
  "WS3",
  "WS4",
  "WS5",
  "WS6",
  "WS7",
  "WS8",
];

export type DefaultsByWorkshop = Partial<
  Record<Workshop["id"], WorkshopOverride>
>;

type DocShape = { byWorkshop: DefaultsByWorkshop; updatedAt: number };

type DefaultsState = {
  byWorkshop: DefaultsByWorkshop;
  isHydrated: boolean;
  loadError: string | null;
  syncState: SyncState;
  lastSyncedAt: number | null;
  syncError: string | null;
  hydrate: () => Promise<void>;
  sync: () => Promise<void>;
  setDefault: (workshopId: Workshop["id"], patch: WorkshopOverridePatch) => void;
  clearDefaultField: (
    workshopId: Workshop["id"],
    field: keyof WorkshopOverridePatch,
  ) => void;
  clearDefaults: (workshopId: Workshop["id"]) => void;
};

let etag: string | null | undefined;

function emptyDefaults(): DefaultsByWorkshop {
  return {};
}

function parseDefaults(raw: unknown): DefaultsByWorkshop {
  if (!raw || typeof raw !== "object") return emptyDefaults();
  const src = raw as Record<string, unknown>;
  const bucket =
    src.byWorkshop && typeof src.byWorkshop === "object"
      ? (src.byWorkshop as Record<string, unknown>)
      : src;
  const out: DefaultsByWorkshop = {};
  for (const id of WORKSHOP_IDS) {
    const entry = bucket[id];
    if (!entry || typeof entry !== "object") continue;
    const sanitised = sanitiseOverride(entry as Record<string, unknown>, id);
    if (!sanitised) continue;
    const { contentMode: _drop, ...rest } = sanitised;
    out[id] = { ...rest, id: OVERRIDE_ID, workshopId: id };
  }
  return out;
}

function toDoc(byWorkshop: DefaultsByWorkshop): DocShape {
  return { byWorkshop, updatedAt: Date.now() };
}

function mergeDefaults(
  remote: DefaultsByWorkshop | null,
  local: DefaultsByWorkshop,
): DefaultsByWorkshop {
  if (!remote) return local;
  const out: DefaultsByWorkshop = { ...remote };
  for (const id of WORKSHOP_IDS) {
    const r = remote[id];
    const l = local[id];
    if (!l) continue;
    if (!r) {
      out[id] = l;
      continue;
    }
    out[id] = (l.updatedAt ?? 0) >= (r.updatedAt ?? 0) ? l : r;
  }
  return out;
}

async function cacheWrite(byWorkshop: DefaultsByWorkshop): Promise<void> {
  try {
    await idbSet(IDB_KEY, toDoc(byWorkshop));
  } catch {
    /* best-effort */
  }
}

async function writeRemote(byWorkshop: DefaultsByWorkshop): Promise<DefaultsByWorkshop | null> {
  if (!isWorkshopBffEnabled()) return byWorkshop;
  const doc = toDoc(byWorkshop);
  const precondition =
    typeof etag === "string"
      ? { ifMatch: etag }
      : etag === undefined
        ? { createOnly: true }
        : {};
  try {
    const { etag: next } = await putJsonWithEtag(WORKSHOPS_TIER, BFF_PATH, doc, precondition);
    etag = next;
    return byWorkshop;
  } catch (err) {
    if (!(err instanceof DocsConflictError)) return null;
    try {
      const { value: remoteRaw, etag: fresh } = await getJsonWithEtag<unknown>(
        WORKSHOPS_TIER,
        BFF_PATH,
      );
      etag = fresh;
      const remote = remoteRaw == null ? null : parseDefaults(remoteRaw);
      const merged = mergeDefaults(remote, byWorkshop);
      const { etag: next } = await putJsonWithEtag(
        WORKSHOPS_TIER,
        BFF_PATH,
        toDoc(merged),
        { ifMatch: fresh ?? undefined },
      );
      etag = next;
      return merged;
    } catch {
      return null;
    }
  }
}

export const useWorkshopContentDefaults = create<DefaultsState>((set, get) => ({
  byWorkshop: emptyDefaults(),
  isHydrated: false,
  loadError: null,
  syncState: isWorkshopBffEnabled() ? "idle" : "disabled",
  lastSyncedAt: null,
  syncError: null,

  hydrate: async () => {
    let cached = emptyDefaults();
    try {
      cached = parseDefaults(await idbGet(IDB_KEY));
    } catch {
      /* ignore */
    }
    if (!isWorkshopBffEnabled()) {
      set({ byWorkshop: cached, isHydrated: true, loadError: null });
      return;
    }
    try {
      const { value: remoteRaw, etag: remoteEtag } = await getJsonWithEtag<unknown>(
        WORKSHOPS_TIER,
        BFF_PATH,
      );
      etag = remoteEtag;
      const remote = remoteRaw == null ? null : parseDefaults(remoteRaw);
      const merged = mergeDefaults(remote, cached);
      await cacheWrite(merged);
      set({ byWorkshop: merged, isHydrated: true, loadError: null });
    } catch {
      set({
        byWorkshop: cached,
        isHydrated: true,
        loadError: "Couldn’t reach the records service — showing your local copy.",
      });
    }
  },

  sync: async () => {
    if (!isWorkshopBffEnabled()) {
      set({ syncState: "disabled", syncError: null });
      return;
    }
    if (!(await isDocsAuthReady())) {
      set({ syncState: "idle", syncError: null });
      return;
    }
    set({ syncState: "syncing", syncError: null });
    try {
      const { value: remoteRaw, etag: remoteEtag } = await getJsonWithEtag<unknown>(
        WORKSHOPS_TIER,
        BFF_PATH,
      );
      etag = remoteEtag;
      const remote = remoteRaw == null ? null : parseDefaults(remoteRaw);
      const merged = mergeDefaults(remote, get().byWorkshop);
      await cacheWrite(merged);
      set({
        byWorkshop: merged,
        syncState: "synced",
        syncError: null,
        lastSyncedAt: Date.now(),
      });
    } catch {
      set({ syncState: "error", syncError: "Couldn’t reach the records service." });
    }
  },

  setDefault: (workshopId, patch) => {
    const prev = get().byWorkshop[workshopId];
    const next: WorkshopOverride = {
      id: OVERRIDE_ID,
      workshopId,
      ...(prev ?? {}),
      ...patch,
      updatedAt: Date.now(),
    };
    delete (next as { contentMode?: unknown }).contentMode;
    const byWorkshop = { ...get().byWorkshop, [workshopId]: next };
    set({ byWorkshop });
    void cacheWrite(byWorkshop);
    void writeRemote(byWorkshop).then((written) => {
      if (written && written !== byWorkshop) set({ byWorkshop: written });
    });
    broadcastSync();
  },

  clearDefaultField: (workshopId, field) => {
    const prev = get().byWorkshop[workshopId];
    if (!prev) return;
    const next = { ...prev, [field]: undefined, updatedAt: Date.now() };
    const byWorkshop = { ...get().byWorkshop, [workshopId]: next };
    set({ byWorkshop });
    void cacheWrite(byWorkshop);
    void writeRemote(byWorkshop);
    broadcastSync();
  },

  clearDefaults: (workshopId) => {
    const byWorkshop = { ...get().byWorkshop };
    delete byWorkshop[workshopId];
    set({ byWorkshop });
    void cacheWrite(byWorkshop);
    void writeRemote(byWorkshop);
    broadcastSync();
  },
}));

export function getWorkshopContentDefault(
  workshopId: string,
): WorkshopOverride | undefined {
  return useWorkshopContentDefaults.getState().byWorkshop[
    workshopId as Workshop["id"]
  ];
}

export function useWorkshopContentDefault(
  workshopId: string,
): WorkshopOverride | undefined {
  return useWorkshopContentDefaults(
    (s) => s.byWorkshop[workshopId as Workshop["id"]],
  );
}
